import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Slide } from 'interfaces/slide';
import React from 'react';

import { caseOrganizationSlideLabel } from './caseOrganizationSlideDismissal';

const assignmentSentence = (count: number) =>
  `${count} ingested ${count === 1 ? 'slide is' : 'slides are'} not assigned to a research case`;

const stainingSentence = (count: number) =>
  `${count} ${count === 1 ? 'slide is' : 'slides are'} missing staining modality`;

export const CaseOrganizationNotice = ({
  unassignedSlides,
  hiddenSlides,
  missingStainingModalityCount,
  showBrightfieldStainInfo,
  canHide,
  savingSlideId,
  error,
  onHide,
  onRestore,
}: {
  unassignedSlides: Slide[];
  hiddenSlides: Slide[];
  missingStainingModalityCount: number;
  showBrightfieldStainInfo: boolean;
  canHide: boolean;
  savingSlideId?: string;
  error?: string;
  onHide: (slideId: string) => void;
  onRestore: (slideId: string) => void;
}) => {
  const confirmation = useConfirmation();
  const noticeParts = [
    unassignedSlides.length ? assignmentSentence(unassignedSlides.length) : null,
    missingStainingModalityCount ? stainingSentence(missingStainingModalityCount) : null,
  ].filter((part): part is string => Boolean(part));
  if (!noticeParts.length && !hiddenSlides.length) {
    return null;
  }

  const hideSlide = async (slide: Slide) => {
    const label = caseOrganizationSlideLabel(slide);
    const confirmed = await confirmation({
      title: `Hide ${label}?`,
      text: `Hide ${label} from Uploads and Case assignment? The slide stays in the study and its ingest history is unchanged. It can be restored from Hidden in Uploads or here.`,
      confirmButtonProps: { title: 'Hide', color: 'warning', variant: 'contained' },
      cancelButtonProps: { title: 'Cancel', color: 'inherit', variant: 'outlined' },
    });
    if (confirmed) {
      onHide(slide.id);
    }
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      sx={{ borderLeft: noticeParts.length ? 2 : 0, borderColor: 'warning.main', pl: noticeParts.length ? 1.5 : 0 }}
    >
      <Stack spacing={0.75} sx={{ maxWidth: 560, minWidth: 0 }}>
        {noticeParts.length ? <Typography variant="subtitle2">Case assignment</Typography> : null}
        {noticeParts.length ? (
          <Alert severity="warning" data-testid="manifest-warning-hint">
            {`${noticeParts.join(
              '; '
            )}. Assign each ingested slide to the correct research case and add any missing staining modality.`}
          </Alert>
        ) : null}
        {noticeParts.length && showBrightfieldStainInfo ? (
          <Alert severity="info" data-testid="manifest-info-hint">
            For H&amp;E and IHC slides, choose the stain recorded for each image. This determines which
            modality-specific analysis workflow applies.
          </Alert>
        ) : null}
        {unassignedSlides.map((slide) => {
          const label = caseOrganizationSlideLabel(slide);
          const isSaving = savingSlideId === slide.id;
          return (
            <Stack key={slide.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ minWidth: 0 }}>
                {label}
              </Typography>
              {canHide ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  aria-label={`Hide ${label}`}
                  disabled={Boolean(savingSlideId)}
                  onClick={() => {
                    void hideSlide(slide);
                  }}
                  startIcon={
                    isSaving ? <CircularProgress color="inherit" size={16} aria-label="Hiding slide" /> : undefined
                  }
                  sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
                >
                  Hide
                </Button>
              ) : null}
            </Stack>
          );
        })}
        {hiddenSlides.map((slide) => {
          const label = caseOrganizationSlideLabel(slide);
          return (
            <Stack key={slide.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                {`${label} hidden from Uploads and Case assignment`}
              </Typography>
              {canHide ? (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  aria-label={`Restore ${label}`}
                  disabled={Boolean(savingSlideId)}
                  onClick={() => onRestore(slide.id)}
                  sx={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
                >
                  Restore
                </Button>
              ) : null}
            </Stack>
          );
        })}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!canHide && (unassignedSlides.length || hiddenSlides.length) ? (
          <Typography variant="caption" color="text.secondary">
            Study editing permission is required to hide a slide.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};
